import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';
import { z } from 'zod';

import {
  paginatedResponseZod,
  PaginationRequest,
  toQueryString,
  wittyUuidZod,
} from './schema';

export const wittyMcpAuthModeZod = z.enum([
  'none',
  'forward',
  'fixed_headers',
  'oauth',
]);

export type WittyMcpAuthMode = z.infer<typeof wittyMcpAuthModeZod>;

export const wittyMcpTransportZod = z.enum(['http', 'sse']);

export type WittyMcpTransport = z.infer<typeof wittyMcpTransportZod>;

export const wittyMcpHeaderZod = z.object({
  name: z.string(),
  value: z.string().nullable(),
  is_sensitive: z.boolean(),
});

export const wittyMcpConnectionZod = z.object({
  id: wittyUuidZod,
  slug: z.string(),
  display_name: z.string(),
  endpoint_url: z.string().nullable(),
  transport: wittyMcpTransportZod,
  enabled: z.boolean(),
  auth_mode: wittyMcpAuthModeZod,
  trusted_forward: z.boolean(),
  is_platform_provided: z.boolean(),
  discovery_status: z.enum(['not_discovered', 'refreshing', 'ready', 'error']),
  discovery_error: z.string().nullable(),
  last_discovered_at: z.string().nullable(),
  header_names: z.array(z.string()).default([]),
  headers: z.array(wittyMcpHeaderZod).default([]),
  tool_count: z.number().int().nonnegative().default(0),
  assignment_count: z.number().int().nonnegative().default(0),
  oauth_client_id: z.string().nullable(),
  oauth_client_secret_configured: z.boolean(),
  oauth_client_metadata_url: z.string().nullable(),
  oauth_scopes: z.array(z.string()).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

export type WittyMcpConnection = z.infer<typeof wittyMcpConnectionZod>;

export const wittyMcpConnectionCreateZod = z.object({
  slug: z.string().min(1).max(255),
  display_name: z.string().min(1).max(255),
  endpoint_url: z.string().min(1),
  transport: wittyMcpTransportZod.default('http'),
  enabled: z.boolean().default(true),
  auth_mode: wittyMcpAuthModeZod.default('none'),
  trusted_forward: z.boolean().default(false),
  oauth_client_id: z.string().nullable().optional(),
  oauth_client_secret: z.string().nullable().optional(),
  oauth_client_metadata_url: z.string().nullable().optional(),
  oauth_scopes: z.array(z.string()).default([]),
  headers: z
    .array(
      z.object({
        name: z.string().min(1).max(255),
        value: z.string().min(1),
        is_sensitive: z.boolean().default(false),
      }),
    )
    .default([]),
});

export type WittyMcpConnectionCreate = z.input<
  typeof wittyMcpConnectionCreateZod
>;

export const wittyMcpHeaderChangeZod = z
  .object({
    name: z.string().min(1).max(255),
    action: z.enum(['replace', 'remove']),
    value: z.string().optional(),
    is_sensitive: z.boolean().default(false),
  })
  .superRefine((change, context) => {
    if (change.action === 'replace' && !change.value) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'A value is required when replacing a header',
      });
    }
  });

export type WittyMcpHeaderChange = z.infer<typeof wittyMcpHeaderChangeZod>;

export const wittyMcpConnectionUpdateZod = z.object({
  display_name: z.string().min(1).max(255).optional(),
  endpoint_url: z.string().min(1).optional(),
  transport: wittyMcpTransportZod.optional(),
  enabled: z.boolean().optional(),
  auth_mode: wittyMcpAuthModeZod.optional(),
  trusted_forward: z.boolean().optional(),
  oauth_client_id: z.string().nullable().optional(),
  oauth_client_secret: z.string().nullable().optional(),
  oauth_client_metadata_url: z.string().nullable().optional(),
  oauth_scopes: z.array(z.string()).optional(),
  headers: z.array(wittyMcpHeaderChangeZod).optional(),
});

export type WittyMcpConnectionUpdate = z.infer<
  typeof wittyMcpConnectionUpdateZod
>;

export type WittyMcpConnectionListRequest = PaginationRequest;

export const wittyUserOAuthConnectionZod = z.object({
  id: wittyUuidZod,
  display_name: z.string(),
  connected: z.boolean(),
});

export type WittyUserOAuthConnection = z.infer<
  typeof wittyUserOAuthConnectionZod
>;

const wittyOAuthConnectResultZod = z.object({
  authorization_url: z.string().url(),
});

const wittyMcpAuthenticationRequiredDetailZod = z.object({
  code: z.literal('authentication_required'),
  connection_id: wittyUuidZod,
  slug: z.string(),
  display_name: z.string(),
});

export type WittyMcpAuthenticationRequiredDetail = z.infer<
  typeof wittyMcpAuthenticationRequiredDetailZod
>;

export class WittyMcpAuthenticationRequiredError extends Error {
  constructor(public readonly detail: WittyMcpAuthenticationRequiredDetail) {
    super(`${detail.display_name} requires OAuth authorization`);
    this.name = 'WittyMcpAuthenticationRequiredError';
  }
}

export const WITTY_MCP_DISCOVERY_RETRY_STORAGE_KEY =
  'witty.mcp.discovery-retry-connection-id';

export interface WittyMcpConnectionsApi {
  listConnections(
    request: WittyMcpConnectionListRequest,
  ): Promise<
    z.infer<
      ReturnType<typeof paginatedResponseZod<typeof wittyMcpConnectionZod>>
    >
  >;
  listConnectionOptions(): Promise<string[]>;
  getConnection(id: string): Promise<WittyMcpConnection>;
  createConnection(
    connection: WittyMcpConnectionCreate,
  ): Promise<WittyMcpConnection>;
  updateConnection(
    id: string,
    update: WittyMcpConnectionUpdate,
  ): Promise<WittyMcpConnection>;
  refreshDiscovery(id: string): Promise<WittyMcpConnection>;
  deleteConnection(id: string, force?: boolean): Promise<void>;
  listUserOAuthConnections(): Promise<WittyUserOAuthConnection[]>;
  connectOAuth(id: string): Promise<string>;
  disconnectOAuth(id: string): Promise<void>;
}

export const wittyMcpConnectionsApiRef: ApiRef<WittyMcpConnectionsApi> =
  createApiRef<WittyMcpConnectionsApi>({
    id: 'plugin.witty-mcp-connections',
  });

export class WittyMcpConnectionsClient implements WittyMcpConnectionsApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('witty');
  }

  private async parseConnection(
    response: Response,
  ): Promise<WittyMcpConnection> {
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const data: unknown = await response.json();
    const parsed = wittyMcpConnectionZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(
        `Invalid MCP connection response: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  }

  async listConnections(
    request: WittyMcpConnectionListRequest,
  ): Promise<
    z.infer<
      ReturnType<typeof paginatedResponseZod<typeof wittyMcpConnectionZod>>
    >
  > {
    const baseUrl = await this.getBaseUrl();
    const query = toQueryString(request);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections${query}`,
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const data: unknown = await response.json();
    const parsed = paginatedResponseZod(wittyMcpConnectionZod).safeParse(data);
    if (!parsed.success) {
      throw new Error(
        `Invalid MCP connections response: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  }

  async listConnectionOptions(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/options`,
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const data: unknown = await response.json();
    const parsed = z.array(z.string()).safeParse(data);
    if (!parsed.success) {
      throw new Error(
        `Invalid MCP connection options response: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  }

  async getConnection(id: string): Promise<WittyMcpConnection> {
    const baseUrl = await this.getBaseUrl();
    return this.parseConnection(
      await this.fetchApi.fetch(
        `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}`,
      ),
    );
  }

  async createConnection(
    connection: WittyMcpConnectionCreate,
  ): Promise<WittyMcpConnection> {
    const baseUrl = await this.getBaseUrl();
    return this.parseConnection(
      await this.fetchApi.fetch(`${baseUrl}/v1/mcp-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(connection),
      }),
    );
  }

  async updateConnection(
    id: string,
    update: WittyMcpConnectionUpdate,
  ): Promise<WittyMcpConnection> {
    const baseUrl = await this.getBaseUrl();
    return this.parseConnection(
      await this.fetchApi.fetch(
        `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update),
        },
      ),
    );
  }

  async refreshDiscovery(id: string): Promise<WittyMcpConnection> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}/discovery`,
      { method: 'POST' },
    );
    if (response.status === 409) {
      try {
        const payload: unknown = await response.clone().json();
        const parsed = z
          .object({ detail: wittyMcpAuthenticationRequiredDetailZod })
          .safeParse(payload);
        if (parsed.success) {
          throw new WittyMcpAuthenticationRequiredError(parsed.data.detail);
        }
      } catch (error) {
        if (error instanceof WittyMcpAuthenticationRequiredError) throw error;
      }
    }
    return this.parseConnection(response);
  }

  async deleteConnection(id: string, force = false): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = force ? '?force=true' : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}${query}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
  }

  async listUserOAuthConnections(): Promise<WittyUserOAuthConnection[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/oauth`,
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const data: unknown = await response.json();
    const parsed = z.array(wittyUserOAuthConnectionZod).safeParse(data);
    if (!parsed.success) {
      throw new Error(`Invalid MCP OAuth response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async connectOAuth(id: string): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}/oauth/connect`,
      { method: 'POST' },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const data: unknown = await response.json();
    const parsed = wittyOAuthConnectResultZod.safeParse(data);
    if (!parsed.success) {
      throw new Error(
        `Invalid MCP OAuth connect response: ${parsed.error.message}`,
      );
    }
    return parsed.data.authorization_url;
  }

  async disconnectOAuth(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/mcp-connections/${encodeURIComponent(id)}/oauth`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
  }
}
